import { useEffect, useCallback } from "react";
import { useProject } from "../../../../hooks";
import { useCart, useLabor as useLaborQuery } from "../../../../hooks/query";
import { useLaborFormulaEvaluator } from "../../hooks";

export interface LaborSelection {
    id?: string;
    CartItemId?: string;
    quantity: number;
    unitPrice?: number;
    supplierId?: string;
    supplierName?: string;
}

interface UseLaborMasterDataInitProps {
    measurements: any[];
    // Setters (owned by the orchestrator hook)
    setLaborSchema: (schema: any[]) => void;
    setCart: (cart: any[]) => void;
    setPriceRanges: (ranges: Record<string, { min: number; max: number }>) => void;
    setIsLoading: (loading: boolean) => void;
    setSelectedValues: (values: Record<string, LaborSelection>) => void;
    initializeDbState: (values: Record<string, LaborSelection>) => void;
}

/**
 * Initialization Hook: Loads labor data from API and hydrates the local UI state.
 */
export default function useLaborMasterDataInit(stateSetters: UseLaborMasterDataInitProps) {
    const { measurements, setLaborSchema, setCart, setPriceRanges, setIsLoading, setSelectedValues, initializeDbState } = stateSetters;

    const { project } = useProject();
    const { useListByProjectId } = useCart();
    const { useListLaborServices, useListLaborPriceRanges } = useLaborQuery();

    const cartListSvc = useListByProjectId();
    const listLaborServicesSvc = useListLaborServices();
    const listLaborPriceRangesSvc = useListLaborPriceRanges();

    const { evaluateFormula } = useLaborFormulaEvaluator();

    const fetchCartItems = useCallback(async () => {
        if (!project?.id) return [];
        return await cartListSvc.mutateAsync({ ProjectId: project.id });
    }, [project?.id, cartListSvc]);

    const getIdentifiedLaborServices = (allServices: any[]) => {
        const formulaResults = evaluateFormula(measurements, allServices, {});
        return {
            identifiedServices: allServices.filter((service: any) => {
                const qty = formulaResults[service.Id];
                return qty !== null && qty !== undefined && qty > 0;
            }),
            formulaResults,
        };
    };

    const parseLaborCartItems = (cartItems: any[]): Record<string, LaborSelection> => {
        const result: Record<string, LaborSelection> = {};
        cartItems
            .filter((item: any) => item.LaborServiceId)
            .forEach((item: any) => {
                result[item.LaborServiceId] = {
                    id: item.Id,
                    CartItemId: item.CartItemId || item.Id,
                    quantity: item.ItemCount || 0,
                    unitPrice: item.UnitPrice || 0,
                    supplierId: item.SupplierId,
                    supplierName: item.SupplierName,
                };
            });
        return result;
    };

    const loadLaborData = useCallback(async () => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const allServices = listLaborServicesSvc.data ?? [];

            const [cartItems, apiRanges] = await Promise.all([
                fetchCartItems(),
                listLaborPriceRangesSvc.mutateAsync()
            ]);

            const ranges: Record<string, { min: number; max: number }> = {};
            apiRanges?.forEach((item: any) => {
                ranges[item.LaborServiceId] = {
                    min: Number(item.MinPrice),
                    max: Number(item.MaxPrice)
                };
            });
            setPriceRanges(ranges);

            const { identifiedServices, formulaResults } = getIdentifiedLaborServices(allServices);
            setLaborSchema(identifiedServices);

            setCart(cartItems);

            const cartSelections = parseLaborCartItems(cartItems);

            // Seed db memory only with what is actually currently inside the database
            initializeDbState(cartSelections);

            // Merge: cart wins on conflict; formula fills in the rest
            const hydratedSelections: Record<string, LaborSelection> = { ...cartSelections };
            identifiedServices.forEach((service: any) => {
                const calculatedQty = formulaResults[service.Id];
                if (!cartSelections[service.Id] && calculatedQty && calculatedQty > 0) {
                    hydratedSelections[service.Id] = { quantity: calculatedQty, unitPrice: 0 };
                }
            });

            setSelectedValues(hydratedSelections);
        } catch (error) {
            console.error("Error loading labor data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        project?.id,
        measurements,
        listLaborServicesSvc.data,
        fetchCartItems,
        listLaborPriceRangesSvc,
        setLaborSchema,
        setCart,
        setPriceRanges,
        setIsLoading,
        setSelectedValues,
        initializeDbState,
    ]);

    useEffect(() => {
        if (Array.isArray(measurements) && measurements.length > 0 && !listLaborServicesSvc.isLoading) {
            loadLaborData();
        }
    }, [measurements, listLaborServicesSvc.isLoading]);

    return {
        loadLaborData,
        fetchCartItems,
    };
}
